import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { getSuperAdminToken, setSuperAdminToken } from '@/api/apiClient';
import {
  LayoutDashboard,
  Building2,
  CreditCard,
  Settings,
  Mail,
  Cpu,
  FileText,
  LogOut,
  Bug,
  RotateCcw,
  MapPinned,
  LifeBuoy,
  HardDrive,
  Link2,
} from 'lucide-react';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarProvider,
  SidebarFooter,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
} from '@/components/ui/sidebar';
import { Button } from '@/components/ui/button';
import ScrollToTopButton from '@/components/ScrollToTopButton';
import { Toaster } from 'sonner';
import { cn } from '@/lib/utils';

const navItems = [
  { to: '/admin/dashboard', end: true, label: 'Dashboard', icon: LayoutDashboard },
  /* Logo após o Dashboard: visível sem scroll e fácil de encontrar após deploy. */
  { to: '/admin/config/guided-tour', end: false, label: 'Tour guiado', icon: MapPinned },
  { to: '/admin/empresas', end: false, label: 'Empresas', icon: Building2 },
  { to: '/admin/recuperar', end: false, label: 'Recuperar', icon: RotateCcw },
  { to: '/admin/planos', end: false, label: 'Planos', icon: CreditCard },
  { to: '/admin/config/asaas', end: false, label: 'Config. ASAAS', icon: Settings },
  { to: '/admin/config/edital-ai', end: false, label: 'Config. IA Editais', icon: Cpu },
  { to: '/admin/config/contratante', end: false, label: 'Config. CONTRATADA', icon: FileText },
  { to: '/admin/config/smtp', end: false, label: 'Config. SMTP', icon: Mail },
  { to: '/admin/config/gdrive', end: false, label: 'Config. Google Drive', icon: HardDrive },
  { to: '/admin/config/certify-gdrive-link', end: false, label: 'Certify ↔ Drive', icon: Link2 },
  { to: '/admin/suporte', end: false, label: 'Suporte', icon: LifeBuoy },
  { to: '/admin/monitoramento-erros', end: false, label: 'Monitoramento de erros', icon: Bug },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const [scrollEl, setScrollEl] = useState(null);
  const token = getSuperAdminToken();

  if (!token) {
    navigate('/admin', { replace: true });
    return null;
  }

  const sair = () => {
    setSuperAdminToken(null);
    navigate('/admin', { replace: true });
  };

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-slate-100">
        <Sidebar
          className="border-r border-slate-700/50 bg-slate-800 text-white [--sidebar-background:theme(colors.slate.800)] [--sidebar-foreground:theme(colors.slate.200)] [--sidebar-accent:theme(colors.slate.700)] [--sidebar-accent-foreground:theme(colors.white)] [--sidebar-border:theme(colors.slate.700)]"
          collapsible="none"
        >
          <SidebarHeader className="border-b border-slate-700/50 p-4">
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-700">
                <Settings className="h-5 w-5 text-slate-300" />
              </div>
              <div>
                <span className="font-semibold text-white">Super Admin</span>
                <p className="text-xs text-slate-400">Painel de gestão</p>
              </div>
            </div>
          </SidebarHeader>

          <SidebarContent className="min-h-0 flex-1 overflow-y-auto p-2">
            <SidebarGroup>
              <SidebarGroupLabel className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
                Navegação
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu className="space-y-0.5">
                  {navItems.map((item) => (
                    <SidebarMenuItem key={item.to}>
                      <SidebarMenuButton asChild tooltip={item.label}>
                        <NavLink
                          to={item.to}
                          end={item.end}
                          className={({ isActive }) =>
                            cn(
                              'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                              isActive
                                ? 'bg-slate-700 text-white'
                                : 'text-slate-200 hover:bg-slate-700/80 hover:text-white'
                            )
                          }
                        >
                          <item.icon className="h-4 w-4 shrink-0" />
                          <span>{item.label}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>

          <SidebarFooter className="border-t border-slate-700/50 p-2">
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 text-slate-200 hover:bg-red-600/20 hover:text-red-400"
              onClick={sair}
            >
              <LogOut className="h-4 w-4" />
              Sair
            </Button>
          </SidebarFooter>
        </Sidebar>

        <main ref={setScrollEl} className="flex-1 overflow-auto">
          <Outlet />
        </main>
        <ScrollToTopButton scrollContainer={scrollEl} />
      </div>
      <Toaster richColors position="top-right" />
    </SidebarProvider>
  );
}
